import { GlobalStyles, StyledEngineProvider, ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import {
  caES as coreCaES,
  enUS as coreEnUS,
  esES as coreEsES,
  ptPT as corePtPT,
} from '@mui/material/locale';
import { createTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/system';
import { enUS as dgEnUS, esES as dgEsES } from '@mui/x-data-grid/locales';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  caES as dpCaES,
  enUS as dpEnUS,
  esES as dpEsES,
  eu as dpEu,
  ptPT as dpPtPT,
} from '@mui/x-date-pickers/locales';
import ErrorFallback from 'components/common/ErrorFallback';
import { GlobalConfirmDialogProvider } from 'components/common/GlobalConfirmDialog';
import PreviewBanner from 'components/common/PreviewBanner';
import React, { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router';
import theme from 'styles/theme';
import { caES as dgCaES } from 'styles/x-data-grid/locales/caES';
import GlobalAlert from '../GlobalAlert';
import Container from './Container';

const locales = {
  es: [
    {
      ...dgEsES,
      components: {
        ...dgEsES.components,
        MuiDataGrid: {
          defaultProps: { localeText: { noRowsLabel: 'Sin resultados.' } },
        },
      },
    },
    coreEsES,
    dpEsES,
  ],
  ca: [dgCaES, coreCaES, dpCaES],
  pt: [corePtPT, dpPtPT],
  eu: [dpEu],
  en: [
    {
      ...dgEnUS,
      components: {
        ...dgEnUS.components,
        MuiDataGrid: {
          defaultProps: { localeText: { noRowsLabel: 'No results.' } },
        },
      },
    },
    coreEnUS,
    dpEnUS,
  ],
};

const momentLocaleMap = { es: 'es', ca: 'ca', en: 'en', pt: 'pt', eu: 'eu' };

const LayoutScene = () => {
  const locale = useSelector(state => state.i18n.locale);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const themeWithLocale = useMemo(
    () => createTheme(theme(prefersDarkMode), ...(locales[locale] || [])),
    [locale, prefersDarkMode],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themeWithLocale}>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={momentLocaleMap[locale] || 'en'}
        >
          {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
          <CssBaseline />
          <GlobalStyles styles={{ body: { color: themeWithLocale.palette.background.paper } }} />
          <Container>
            <PreviewBanner />
            <Helmet>
              <html lang={locale} />
              <meta httpEquiv="Content-Language" content={locale} />
            </Helmet>
            <Sentry.ErrorBoundary
              fallback={({ error, resetError }) => (
                <ErrorFallback error={error} resetError={resetError} />
              )}
            >
              <GlobalConfirmDialogProvider>
                <Outlet />
              </GlobalConfirmDialogProvider>
              <GlobalAlert />
            </Sentry.ErrorBoundary>
          </Container>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default LayoutScene;
